// Different storage options, TTL?
// Default session, must opt-in to local (with expiration),
// Check enterprise-features cached at gateway, look into list of custodians, income/net worth intervals, both gateway + local?
// Could we mix in other features, like automatic retries? Or always invoke but fall back to cached value?
// If we pass a named (non-anonymous) function, could we make `id` optional and use the name? Then options can be omitted for simple cases.
// Async vs. sync? Could be an option, could be a separate method (probably separate, I don't anticipate a lot of synchronous use cases + slightly easier to type / implement).
// Should we limit the size / number of items in storage for a given key?

// Sample
// const dummyFn = (a: number, b: number): Promise<number> =>
//   new Promise((resolve) => {
//     const val = a + b;

//     resolve(val);
//   });

// memoize(dummyFn, {id: 'dummy'});

type FnArgs = unknown[];
type Fn = (...args: FnArgs) => Promise<unknown>;
type KeyResolver<T extends Fn> = (...args: Parameters<T>) => string;

interface MemoizeOptions<T extends Fn> {
  id: string;
  resolver?: KeyResolver<T>;
}

// TODO: If any of the args are an object, can we iterate through its own properties?
const getKey = (args: FnArgs): string => (args?.length ? args.join('_') : 'emptyargs');

export const memoize =
  <T extends Fn>(fn: T, options: MemoizeOptions<T>) =>
  // @ts-expect-error ts-1064 (async method must return Promise<U>. TS isn't smart enough to know that ReturnType<T> is a promise here without complicating the typings)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async (...args: Parameters<T>): any => {
    const key = `${options.id}_${options.resolver?.(...args) ?? getKey(args)}`;

    try {
      const fromStorage = sessionStorage.getItem(key);

      if (fromStorage) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return JSON.parse(fromStorage) as any;
      }

      const result = await fn(...args);

      sessionStorage.setItem(key, JSON.stringify(result));

      return result;
    } catch (error) {
      sessionStorage.removeItem(key);

      throw error;
    }
  };

// Notes (8/9/2021):
//   * Only async is implemented at the moment - function must return a promise
//   * Only sessionStorage is supported at the moment
//   * The default key resolver only handles simple types at the moment - if the function takes objects, arrays, etc. as parameters, you must provide your own resolver.
